import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { CCTable } from "components/ui/Table";
import { formatCurrency } from "../../../functions/formatCurrency";
import ButtonAsLink from "components/ui/ButtonAsLink";
import InfoIcon from "components/ui/InfoIcon";
import StyledMessage from "components/ui/StyledMessage";
import { shouldShowDecimals } from "./showDecimals";
import Popover from "components/ui/Popover";
import { useUser } from "components/hooks/useUser";
import { VisuallyHidden } from "components/ui/VisuallyHidden";

export function NetCost({ currentYearAid }) {
  const {
    financialAidSummary: { messages = {} },
  } = useUser((user) => user.financialAidSummary || {});

  const {
    totalCostOfAttendance,
    totalGiftAidAndWaivers,
    totalThirdPartyContracts,
    totalNetCost,
  } = currentYearAid;

  const {
    giftAidMoreInfoMessage,
    netCostMoreInfoMessage,
    thirdPartyMoreInfoMessage,
  } = messages;

  const showDecimals = shouldShowDecimals(currentYearAid);

  const [expandedPopover, setExpandedPopover] = useState(null);

  const togglePopover = (popoverId) => {
    setExpandedPopover((current) => (current === popoverId ? null : popoverId));
  };

  return (
    <div
      style={{
        backgroundColor: `var(--wild-sand)`,
        padding: `var(--small-spacing) var(--medium-spacing)`,
      }}
    >
      <NetCostTable>
        <tbody>
          <tr>
            <th>Estimated Cost of Attendance</th>
            <td>{formatCurrency(totalCostOfAttendance, { showDecimals })}</td>
          </tr>
          <RowWithMessage
            title="Gift Aid and Waivers"
            message={giftAidMoreInfoMessage}
            showDecimals={showDecimals}
            expanded={expandedPopover === "giftAid"}
            togglePopover={() => togglePopover("giftAid")}
            value={totalGiftAidAndWaivers}
          />
          <RowWithMessage
            title="Third Party Contracts"
            message={thirdPartyMoreInfoMessage}
            showDecimals={showDecimals}
            expanded={expandedPopover === "thirdParty"}
            togglePopover={() => togglePopover("thirdParty")}
            value={totalThirdPartyContracts}
          />
          <RowWithMessage
            title={"Net Cost"}
            message={netCostMoreInfoMessage}
            showDecimals={showDecimals}
            expanded={expandedPopover === "netCost"}
            togglePopover={() => togglePopover("netCost")}
            value={totalNetCost}
          ></RowWithMessage>
        </tbody>
      </NetCostTable>
    </div>
  );
}

NetCost.propTypes = {
  currentYearAid: PropTypes.object,
};

const NetCostTable = styled(CCTable)`
  tbody tr {
    th {
      font-weight: normal;
      color: var(--tundora);
    }

    td {
      text-align: right;
      white-space: nowrap;
    }
  }
`;

const RowWithMessage = ({
  title,
  message,
  showDecimals,
  value,
  expanded,
  togglePopover,
}) => {
  return (
    <tr>
      <th>
        {title}{" "}
        <ButtonAsLink onClick={togglePopover}>
          <InfoIcon /> <VisuallyHidden>Show more about {title}</VisuallyHidden>
        </ButtonAsLink>
        <div style={{ position: `relative` }}>
          {expanded && (
            <Popover title={title} close={togglePopover}>
              <StyledMessage
                style={{ marginTop: `0` }}
                dangerouslySetInnerHTML={{ __html: message }}
              />
            </Popover>
          )}
        </div>
      </th>
      <td>{formatCurrency(value, { showDecimals })}</td>
    </tr>
  );
};

RowWithMessage.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  message: PropTypes.string,
  showDecimals: PropTypes.bool,
  value: PropTypes.number,
  expanded: PropTypes.bool,
  togglePopover: PropTypes.func,
};
